import {
  type ActionData,
  type AnyArmorRoll,
  type ArmorProfileData,
  type DefenseProfileData,
  type EffectMap,
  type ItemProfileData,
  type KnightProfileData,
  type Ref,
  Ruleset,
  type ShieldProfileData,
  type UpgradeProfileData,
  type WeaponProfileData,
} from "./Ruleset";

function armorRoll(
  type: AnyArmorRoll["type"],
  min: number,
  max: number,
): AnyArmorRoll {
  return { type, min, max };
}

function addActionModifier(
  action: Ref | Ref[],
  value: number,
): EffectMap["ADD_ACTION_MODIFIER"] {
  return { type: "ADD_ACTION_MODIFIER", action, value };
}

function addDefenseModifier(
  defense: Ref | Ref[],
  value: number,
): EffectMap["ADD_DEFENSE_MODIFIER"] {
  return { type: "ADD_DEFENSE_MODIFIER", defense, value };
}

function addSpecialRule(
  group: EffectMap["ADD_SPECIAL_RULE"]["group"],
  value: string,
  options?: { hideInSummary?: boolean },
): EffectMap["ADD_SPECIAL_RULE"] {
  return { type: "ADD_SPECIAL_RULE", group, value, ...options };
}

function gainAction(action: Ref): EffectMap["GAIN_ACTION"] {
  return { type: "GAIN_ACTION", action };
}

function removeDefense(defense: Ref | Ref[]): EffectMap["REMOVE_DEFENSE"] {
  return { type: "REMOVE_DEFENSE", defense };
}

function removeAction(action: Ref): EffectMap["REMOVE_ACTION"] {
  return { type: "REMOVE_ACTION", action };
}

function addMomentumLimitModifier(
  value: number,
): EffectMap["ADD_MOMENTUM_LIMIT_MODIFIER"] {
  return { type: "ADD_MOMENTUM_LIMIT_MODIFIER", value };
}

export function ref(item: { id: string }) {
  return { id: item.id };
}

export function refs(items: { id: string }[]) {
  return items.map(ref);
}

export const block: DefenseProfileData = {
  id: "NNI6iM",
  name: "Block",
};

export const dodge: DefenseProfileData = {
  id: "ukUkRs",
  name: "Dodge",
};

export const parry: DefenseProfileData = {
  id: "jgoTR5",
  name: "Parry",
};

export const riposte: DefenseProfileData = {
  id: "TxFP5X",
  name: "Riposte",
};

export const charge: ActionData = {
  id: "8HWccy",
  name: "Charge",
  description: "Move up to 6” and gain 2 momentum. Must contact enemy.",
  unit: "INCHES",
  isDefaultAction: true,
};

export const sprint: ActionData = {
  id: "7aVMX3",
  name: "Sprint",
  description: "Move up to 5” and gain 2 momentum. Cannot contact enemy.",
  unit: "INCHES",
  isDefaultAction: true,
};

export const engage: ActionData = {
  id: "oshMQW",
  name: "Engage",
  description: "Move up to 4” and gain 1 momentum. Must contact enemy.",
  unit: "INCHES",
  isDefaultAction: true,
};

export const encircle: ActionData = {
  id: "BWWK56",
  name: "Encircle",
  description: "Move up to 3” and gain 1 momentum. Cannot contact enemy.",
  unit: "INCHES",
  isDefaultAction: true,
};

export const bash: ActionData = {
  id: "Jw7G9m",
  name: "Bash",
  description:
    "Success moves the defender up to 3” and they lose all momentum.",
  hasClashTest: true,
  isDefaultAction: true,
};

export const item: ActionData = {
  id: "xHUQPo",
  name: "Item",
  description: "Use an equipped item.",
  isDefaultAction: true,
};

export const lunge: ActionData = {
  id: "IOvkLF",
  name: "Lunge",
  description: "Move up to 2”. Must contact enemy",
  isDefaultAction: true,
  unit: "INCHES",
};

export const shift: ActionData = {
  id: "OPtggE",
  name: "Shift",
  description: "Move up to 1”. Cannot contact enemy",
  isDefaultAction: true,
  unit: "INCHES",
};

export const lightMelee: ActionData = {
  id: "xQiihQ",
  name: "Light Melee",
  description: "Success deals 1 damage to the defender.",
  hasClashTest: true,
  isDefaultAction: true,
};

export const lightRanged: ActionData = {
  id: "ne5DZI",
  name: "Light Ranged",
  description: "Success deals 1 damage to the defender.",
  hasClashTest: true,
  isDefaultAction: false,
};

export const heavyMelee: ActionData = {
  id: "RN5rvi",
  name: "Heavy Melee",
  description: "Success deals 2 damage to the defender.",
  hasClashTest: true,
  isDefaultAction: true,
};

export const heavyRanged: ActionData = {
  id: "6cedcC",
  name: "Heavy Ranged",
  description: "Success deals 2 damage to the defender.",
  hasClashTest: true,
  isDefaultAction: false,
};

export const interact: ActionData = {
  id: "WQvEtH",
  name: "Interact",
  description: "Interact with objective or terrain.",
  isDefaultAction: true,
};

export const shout: ActionData = {
  id: "n3ZMwt",
  name: "Shout",
  description:
    "Gain 1 momentum and roll one die per ally within 3”. On a 4+, they gain 1 momentum. You may only perform this action twice per round.",
  isDefaultAction: true,
};

// ---- Weapon traits ----

const complexWeapon = {
  id: "nbOLWM",
  name: "Complex weapon",
  description: `In order to attack with a complex weapon, you must spend an additional die of the same value before the action can be performed. (e.g. it takes two results of 1 to shoot a crossbow).`,
  effects: [
    addSpecialRule(
      "ATTACK",
      "Must spend an additional die of the same value before an attack action can be performed.",
      { hideInSummary: true },
    ),
  ],
};

const massiveWeapon = {
  id: "gGLewf",
  name: "Massive weapon",
  description: `Knights with massive weapons can only perform heavy attacks, and only if the attacking knight has at least 1 momentum before initiating the clash test. Knights with massive weapons may still perform bash actions without restriction.`,
  effects: [
    removeAction(ref(lightMelee)),
    addSpecialRule(
      "ATTACK",
      "Must have at least 1 momentum to perform the Heavy Melee action.",
      { hideInSummary: true },
    ),
  ],
};

// ---- One-handed weapons ----

const armingSword: WeaponProfileData = {
  id: "tOo1hX",
  name: "Arming Sword",
  type: "ONE_HANDED",
  effects: [addActionModifier(ref(lightMelee), 1)],
};

const handAx: WeaponProfileData = {
  id: "u58HDK",
  name: "Hand Ax",
  type: "ONE_HANDED",
  effects: [addActionModifier(ref(heavyMelee), 1), removeDefense(ref(riposte))],
};

const mace: WeaponProfileData = {
  id: "Lwd46X",
  name: "Mace",
  type: "ONE_HANDED",
  effects: [
    addSpecialRule(
      "ATTACK",
      "Successful light melee attacks may move the defender up to 1″.",
    ),
    removeDefense(ref(riposte)),
  ],
};

const falchion: WeaponProfileData = {
  id: "JVWMZ4",
  name: "Falchion",
  type: "ONE_HANDED",
  effects: [
    addSpecialRule(
      "ATTACK",
      "Successful light melee attacks remove 2 momentum from the defender.",
    ),
  ],
};

const warhammer: WeaponProfileData = {
  id: "roNdb1",
  name: "Warhammer",
  type: "ONE_HANDED",
  effects: [
    addSpecialRule(
      "ATTACK",
      "Successful heavy melee attacks may move the defender up to 1″.",
    ),
    removeDefense(ref(riposte)),
  ],
};

const flail: WeaponProfileData = {
  id: "NiMAHN",
  name: "Flail",
  type: "ONE_HANDED",
  effects: [
    addActionModifier(ref(lightMelee), 1),
    addSpecialRule("ATTACK", "Defender cannot riposte defense."),
    removeDefense(refs([riposte, parry])),
  ],
};

const shortSpear: WeaponProfileData = {
  id: "Bq26pX",
  name: "Short Spear",
  type: "ONE_HANDED",
  effects: [
    addSpecialRule(
      "ATTACK",
      "Counts as an allied knight during a clash test when in base contact with the attacker.",
    ),
    removeDefense(ref(riposte)),
    addSpecialRule("DEFENSE", "Parry defense only gains 1 momentum."),
  ],
};

// ---- Two-handed weapons ----

const greatAx: WeaponProfileData = {
  id: "yFBpLI",
  name: "Great Ax",
  type: "TWO_HANDED",
  effects: [
    addActionModifier(ref(heavyMelee), 1),
    addSpecialRule(
      "ATTACK",
      "Defenders without momentum cannot gain the block defense bonus.",
    ),
    removeDefense(ref(riposte)),
  ],
};

const longSword: WeaponProfileData = {
  id: "7sCPtw",
  name: "Long Sword",
  type: "TWO_HANDED",
  effects: [
    addActionModifier(ref(lightMelee), 1),
    addDefenseModifier(ref(riposte), 1),
  ],
};

const longSpear: WeaponProfileData = {
  id: "WCuPsY",
  name: "Long Spear",
  type: "TWO_HANDED",
  effects: [
    addActionModifier(ref(lightMelee), 1),
    addSpecialRule(
      "ATTACK",
      "Counts as an allied knight during a clash test when in base contact with the attacker or defender.",
    ),
    removeDefense(ref(riposte)),
    addSpecialRule("DEFENSE", "Parry defense only gains 1 momentum."),
  ],
};

const kriegsmesser: WeaponProfileData = {
  id: "otNqFj",
  name: "Kriegsmesser",
  type: "TWO_HANDED",
  effects: [
    addActionModifier(ref(heavyMelee), 1),
    addSpecialRule(
      "ATTACK",
      "Successful heavy melee attacks remove 2 momentum from the defender.",
    ),
  ],
};

const warmaul: WeaponProfileData = {
  id: "Ejb53j",
  name: "Warmaul",
  type: "TWO_HANDED",
  effects: [
    addActionModifier(ref(heavyMelee), 1),
    addSpecialRule(
      "ATTACK",
      "Successful light melee and heavy melee attacks may move the defender up to 2″.",
    ),
    removeDefense(ref(riposte)),
  ],
};

const warFlail: WeaponProfileData = {
  id: "k9PQwR",
  name: "War Flail",
  type: "TWO_HANDED",
  effects: [
    addActionModifier(refs([lightMelee, heavyMelee]), 1),
    addSpecialRule("ATTACK", "Defender cannot riposte defense."),
    removeDefense(refs([riposte, parry])),
  ],
};

const polearm: WeaponProfileData = {
  id: "OXhIo3",
  name: "Polearm",
  type: "TWO_HANDED",
  effects: [
    addActionModifier(ref(heavyMelee), 1),
    addSpecialRule(
      "ATTACK",
      "Counts as an allied knight during a clash test when in base contact with the attacker or defender.",
    ),
    removeDefense(ref(riposte)),
    addSpecialRule("DEFENSE", "Parry defense only gains 1 momentum."),
  ],
};

const zweihander: WeaponProfileData = {
  id: "ZwIh4n",
  name: "Zweihander",
  type: "TWO_HANDED",
  traits: [ref(massiveWeapon)],
  effects: [
    addActionModifier(ref(heavyMelee), 1),
    addSpecialRule(
      "ATTACK",
      "Defender cannot riposte or parry defense. Defenders without momentum cannot gain the block defense bonus.",
    ),
    removeDefense(ref(riposte)),
  ],
};

// ---- Two-handed ranged weapons ----

const bow: WeaponProfileData = {
  id: "xoyjiJ",
  name: "Bow & Dagger",
  type: "TWO_HANDED_RANGED",
  effects: [gainAction(ref(lightRanged)), removeDefense(ref(riposte))],
};

const crossbow: WeaponProfileData = {
  id: "OSigBs",
  name: "Crossbow & Dagger",
  type: "TWO_HANDED_RANGED",
  traits: [ref(complexWeapon)],
  effects: [gainAction(ref(heavyRanged)), removeDefense(ref(riposte))],
};

// ---- Armor ----

const heavyArmor: ArmorProfileData = {
  id: "ucrqvC",
  name: "Heavy Armor",
  rolls: [
    armorRoll("CRIT_FAIL", 1, 1),
    armorRoll("FAIL", 2, 5),
    armorRoll("SAVE", 6, 6),
  ],
};

const mediumArmor: ArmorProfileData = {
  id: "hMVmEH",
  name: "Medium Armor",
  rolls: [
    armorRoll("CRIT_FAIL", 1, 2),
    armorRoll("FAIL", 3, 5),
    armorRoll("SAVE", 6, 6),
  ],
  effects: [addDefenseModifier(ref(dodge), 1)],
};

const lightArmor: ArmorProfileData = {
  id: "BaJIWu",
  name: "Light Armor",
  rolls: [
    armorRoll("CRIT_FAIL", 1, 3),
    armorRoll("FAIL", 4, 5),
    armorRoll("SAVE", 6, 6),
  ],
  effects: [
    addActionModifier(
      refs([charge, sprint, engage, encircle, lunge, shift]),
      1,
    ),
    addDefenseModifier(ref(dodge), 2),
  ],
};

const clothing: ArmorProfileData = {
  id: "cLthNG",
  name: "Clothing",
  rolls: [
    armorRoll("CRIT_FAIL", 1, 4),
    armorRoll("FAIL", 5, 5),
    armorRoll("SAVE", 6, 6),
  ],
  effects: [
    addActionModifier(
      refs([charge, sprint, engage, encircle, lunge, shift]),
      2,
    ),
    addDefenseModifier(ref(dodge), 2),
  ],
};

const nude: ArmorProfileData = {
  id: "nUd3XX",
  name: "Nude",
  rolls: [armorRoll("CRIT_FAIL", 1, 5), armorRoll("SAVE", 6, 6)],
  effects: [
    addActionModifier(
      refs([charge, sprint, engage, encircle, lunge, shift]),
      2,
    ),
    addDefenseModifier(ref(dodge), 3),
  ],
};

// ---- Shields ----

const buckler: ShieldProfileData = {
  id: "bUklER",
  name: "Buckler",
  effects: [addDefenseModifier(ref(block), 1), addActionModifier(ref(bash), 1)],
};

const heater: ShieldProfileData = {
  id: "hEatER",
  name: "Heater Shield",
  effects: [addDefenseModifier(ref(block), 2)],
};

const kite: ShieldProfileData = {
  id: "kItE00",
  name: "Kite Shield",
  effects: [
    addDefenseModifier(ref(block), 2),
    addSpecialRule("DEFENSE", "+3 to block defense against ranged attacks."),
    addMomentumLimitModifier(-1),
  ],
};

// ---- Knight profiles ----

const knightProfile: KnightProfileData = {
  id: "6N4PR4",
  name: "Knight",
  damageLimit: 3,
};

const sergeant: KnightProfileData = {
  id: "Eu65Mi",
  name: "Sergeant",
  damageLimit: 2,
};

// ---- Upgrades ----

const balancedLightWeaponUpgrade: UpgradeProfileData = {
  id: "IQMiu3",
  name: "Balanced Light weapon upgrade",
  type: "WEAPON_UPGRADE",
  effects: [
    addSpecialRule(
      "ATTACK",
      "Re-roll results of 1 during clash tests when performing a Light melee attack.",
    ),
  ],
};

const balancedHeavyWeaponUpgrade: UpgradeProfileData = {
  id: "bHvyWU",
  name: "Balanced Heavy weapon upgrade",
  type: "WEAPON_UPGRADE",
  effects: [
    addSpecialRule(
      "ATTACK",
      "Re-roll results of 1 during clash tests when performing a Heavy melee attack.",
    ),
  ],
};

const polishedWeaponUpgrade: UpgradeProfileData = {
  id: "pOlshW",
  name: "Polished weapon upgrade",
  type: "WEAPON_UPGRADE",
  effects: [
    addSpecialRule(
      "DEFENSE",
      "Re-roll results of 1 during clash tests when defending with a riposte.",
    ),
  ],
};

const stonewashedWeaponUpgrade: UpgradeProfileData = {
  id: "stnWsh",
  name: "Stonewashed weapon upgrade",
  type: "WEAPON_UPGRADE",
  effects: [
    addSpecialRule(
      "DEFENSE",
      "Re-roll results of 1 during clash tests when defending with a parry.",
    ),
  ],
};

const heatTreatmentUpgrade: UpgradeProfileData = {
  id: "htTrtU",
  name: "Heat treatment upgrade",
  type: "WEAPON_UPGRADE",
  effects: [
    addSpecialRule(
      "ATTACK",
      "When attacking or defending, if the clash test results in a fumbled clash, the clash test ends and you win.",
    ),
  ],
};

const temperedWeaponUpgrade: UpgradeProfileData = {
  id: "tmprdW",
  name: "Tempered weapon upgrade",
  type: "WEAPON_UPGRADE",
  effects: [
    addSpecialRule(
      "ATTACK",
      "When attacking or defending, if the clash test results in a perfect clash, the clash test ends and you win.",
    ),
  ],
};

const reinforcedShieldUpgrade: UpgradeProfileData = {
  id: "rnfcSh",
  name: "Reinforced",
  type: "SHIELD_UPGRADE",
  effects: [
    addSpecialRule(
      "DEFENSE",
      "Re-roll results of 1 during clash tests when defending with a block.",
    ),
  ],
};

const improvedEnarmes: UpgradeProfileData = {
  id: "impEnm",
  name: "Improved Enarmes",
  type: "SHIELD_UPGRADE",
  effects: [
    addSpecialRule(
      "ATTACK",
      "Re-roll results of 1 during clash tests when performing the bash action.",
    ),
  ],
};

// ---- Items ----

// Worn
const sapphireRing: ItemProfileData = {
  id: "sphRng",
  name: "Sapphire Ring",
  type: "WORN",
  effects: [addActionModifier(refs([lightMelee, lightRanged]), 1)],
};

const rubyRing: ItemProfileData = {
  id: "rubyRg",
  name: "Ruby Ring",
  type: "WORN",
  effects: [addActionModifier(refs([heavyMelee, heavyRanged]), 1)],
};

const jetRing: ItemProfileData = {
  id: "jetRng",
  name: "Jet Ring",
  type: "WORN",
  effects: [addSpecialRule("PASSIVE", "+1 to courage test.")],
};

const citrineRing: ItemProfileData = {
  id: "ctrRng",
  name: "Citrine Ring",
  type: "WORN",
  effects: [addDefenseModifier(ref(parry), 1)],
};

const amethystRing: ItemProfileData = {
  id: "amtRng",
  name: "Amethyst Ring",
  type: "WORN",
  effects: [addDefenseModifier(ref(dodge), 1)],
};

const fireOpalRing: ItemProfileData = {
  id: "foRing",
  name: "Fire Opal Ring",
  type: "WORN",
  effects: [addDefenseModifier(ref(riposte), 1)],
};

const peridotAmulet: ItemProfileData = {
  id: "prdAml",
  name: "Peridot Amulet",
  type: "WORN",
  effects: [
    addSpecialRule("PASSIVE", "Hazard table rolls of 3-6 regain your footing."),
  ],
};

const emeraldAmulet: ItemProfileData = {
  id: "emrAml",
  name: "Emerald Amulet",
  type: "WORN",
  effects: [
    addSpecialRule(
      "ARMOR",
      "Armor table roll of 3-6 count as Impervious! against ranged attacks.",
    ),
  ],
};

const moonstoneAmulet: ItemProfileData = {
  id: "mnAml",
  name: "Moonstone Amulet",
  type: "WORN",
  effects: [
    addSpecialRule("DEFENSE", "Damage from elevation 2 and 3 is reduced by 1."),
  ],
};

const sturdyBelt: ItemProfileData = {
  id: "strdBl",
  name: "Sturdy Belt",
  type: "WORN",
  effects: [
    addSpecialRule("DEFENSE", "Gain +1 to clash test when outnumbered."),
  ],
};

const agedBelt: ItemProfileData = {
  id: "agdBlt",
  name: "Aged Belt",
  type: "WORN",
  effects: [
    addSpecialRule("DEFENSE", "Bash action only removes 1 momentum from you."),
  ],
};

const fineBelt: ItemProfileData = {
  id: "fneBlt",
  name: "Fine Belt",
  type: "WORN",
  effects: [addSpecialRule("DEFENSE", "Bash action only moves you 2″.")],
};

const whiteOrle: ItemProfileData = {
  id: "whtOrl",
  name: "White Orle",
  type: "WORN",
  effects: [
    addSpecialRule(
      "PASSIVE",
      "When you pass a courage test, all allied knights within 1″ automatically pass their courage test.",
    ),
  ],
};

const colorfulOrle: ItemProfileData = {
  id: "clrOrl",
  name: "Colorful Orle",
  type: "WORN",
  effects: [
    addSpecialRule(
      "PASSIVE",
      "When you pass a courage test, immediately distribute 3 momentum within 3″.",
    ),
  ],
};

const blackOrle: ItemProfileData = {
  id: "blkOrl",
  name: "Black Orle",
  type: "WORN",
  effects: [
    addSpecialRule(
      "PASSIVE",
      "When you pass a courage test, the nearest enemy knight within 1″ immediately takes a courage test.",
    ),
  ],
};

// Usable
const banner: ItemProfileData = {
  id: "ZkIpQd",
  name: "Banner",
  type: "USABLE",
  effects: [
    addSpecialRule(
      "USEABLE",
      "When used, an ally within 3″ may immediately perform an encircle move action.",
    ),
  ],
};

const throwingAxe: ItemProfileData = {
  id: "thrwAx",
  name: "Throwing Ax",
  type: "USABLE",
  effects: [
    addSpecialRule(
      "USEABLE",
      "When used, you may remove 2 momentum from an enemy knight within 3″ and in line of sight.",
    ),
  ],
};

const battlefieldOrders: ItemProfileData = {
  id: "bfOrdr",
  name: "Battlefield Orders",
  type: "USABLE",
  effects: [
    addSpecialRule(
      "USEABLE",
      "When used, an ally within 3″ may immediately perform a lunge move action.",
    ),
  ],
};

const relic: ItemProfileData = {
  id: "relicX",
  name: "Relic",
  type: "USABLE",
  effects: [
    addSpecialRule(
      "USEABLE",
      "When used, you may remove 1 ally gruesomely slain marker within 3″.",
    ),
  ],
};

const horn: ItemProfileData = {
  id: "hornXX",
  name: "Horn",
  type: "USABLE",
  effects: [
    addSpecialRule(
      "USEABLE",
      "When used, you may redistribute 3 momentum within 6″.",
    ),
  ],
};

const loversHandkerchief: ItemProfileData = {
  id: "lvsHnk",
  name: "Lovers Handkerchief",
  type: "USABLE",
  effects: [addSpecialRule("USEABLE", "When used, you gain 3 momentum.")],
};

// Consumables
const jasminOils: ItemProfileData = {
  id: "jsmOil",
  name: "Jasmin Oils",
  type: "CONSUMABLE_BUFF",
  effects: [
    addSpecialRule("USEABLE", "Use to gain +2 on your next clash test."),
  ],
};

const roseOils: ItemProfileData = {
  id: "rseOil",
  name: "Rose Oils",
  type: "CONSUMABLE_BUFF",
  effects: [
    addSpecialRule("USEABLE", "Use to gain +2 on your next courage test."),
  ],
};

const lavenderOils: ItemProfileData = {
  id: "lvdOil",
  name: "Lavender Oils",
  type: "CONSUMABLE_BUFF",
  effects: [
    addSpecialRule("USEABLE", "Use to gain +2 on your next dodge defense."),
  ],
};

const healingHerb: ItemProfileData = {
  id: "spVhNM",
  name: "Healing Herb",
  type: "CONSUMABLE_INSTANT",
  effects: [
    addSpecialRule(
      "USEABLE",
      "Use to immediately remove 1 damage and gain 2 momentum.",
    ),
  ],
};

const medicinalTincture: ItemProfileData = {
  id: "mdTnct",
  name: "Medicinal Tincture",
  type: "CONSUMABLE_INSTANT",
  effects: [addSpecialRule("USEABLE", "Use to immediately remove 2 damage.")],
};

const alacrityTincture: ItemProfileData = {
  id: "alTnct",
  name: "Alacrity Tincture",
  type: "CONSUMABLE_INSTANT",
  effects: [
    addSpecialRule(
      "USEABLE",
      "Use to immediately perform either an engage or encircle move action.",
    ),
  ],
};

const firePowderPot: ItemProfileData = {
  id: "firPot",
  name: "Fire Powder Pot",
  type: "CONSUMABLE_TOSSED",
  effects: [
    addSpecialRule(
      "USEABLE",
      "Use to throw up to 6″. Knights in contact with the fire marker are forced to perform a shift move action, then discard the marker.",
    ),
  ],
};

const smokePowderPot: ItemProfileData = {
  id: "smkPot",
  name: "Smoke Powder Pot",
  type: "CONSUMABLE_TOSSED",
  effects: [
    addSpecialRule(
      "USEABLE",
      "Use to throw up to 3″. Knights in contact with the smoke marker cannot perform light melee or heavy melee attack actions.",
    ),
  ],
};

const flashPowderPot: ItemProfileData = {
  id: "flshPt",
  name: "Flash Powder Pot",
  type: "CONSUMABLE_TOSSED",
  effects: [
    addSpecialRule(
      "USEABLE",
      "Use to throw up to 3″. Knights in contact with the flash marker immediately lose all momentum, then discard the marker.",
    ),
  ],
};

export const latest = new Ruleset({
  version: "0.4.1",

  maxQuickBattleKnights: 6,
  maxQuickBattleWornAndUsables: 2,
  maxQuickBattleConsumables: 1,

  defenses: [block, dodge, parry, riposte],

  weaponTraits: [complexWeapon, massiveWeapon],

  initiativeSteps: [
    {
      id: "GK45oO",
      name: "Run Actions",
      value: 6,
      actions: [charge, sprint],
    },
    {
      id: "WxdlPL",
      name: "Walk Actions",
      value: 5,
      actions: [engage, encircle],
    },
    {
      id: "dL0KNZ",
      name: "Bash Actions",
      value: 4,
      actions: [bash, item],
    },
    {
      id: "3MYgrx",
      name: "Step Actions",
      value: 3,
      actions: [lunge, shift],
    },
    {
      id: "71eVrL",
      name: "Fast Attacks",
      value: 2,
      actions: [lightMelee, lightRanged],
    },
    {
      id: "STqiw2",
      name: "Heavy Attacks",
      value: 1,
      actions: [heavyMelee, heavyRanged, interact],
    },
    {
      id: "36hywS",
      name: "Battle Cry!",
      value: "ANY",
      actions: [shout],
    },
  ],

  defaultKnight: {
    name: "",
    profile: ref(knightProfile),
    armor: ref(heavyArmor),
    weapons: refs([armingSword]),
    shields: refs([heater]),
    items: [],
    wearables: [],
    upgrades: [],
  },

  knightProfiles: [knightProfile, sergeant],

  weaponProfiles: [
    // One-handed
    armingSword,
    handAx,
    mace,
    falchion,
    warhammer,
    flail,
    shortSpear,

    // Two-handed
    greatAx,
    longSword,
    longSpear,
    kriegsmesser,
    warmaul,
    warFlail,
    polearm,
    zweihander,

    // Two-handed ranged
    bow,
    crossbow,
  ],

  armorProfiles: [heavyArmor, mediumArmor, lightArmor, clothing, nude],

  shieldProfiles: [heater, buckler, kite],

  wearableProfiles: [
    sapphireRing,
    rubyRing,
    jetRing,
    citrineRing,
    amethystRing,
    fireOpalRing,
    peridotAmulet,
    emeraldAmulet,
    moonstoneAmulet,
    sturdyBelt,
    agedBelt,
    fineBelt,
    whiteOrle,
    colorfulOrle,
    blackOrle,
  ],

  itemProfiles: [
    // Usable
    banner,
    throwingAxe,
    battlefieldOrders,
    relic,
    horn,
    loversHandkerchief,

    // Consumables
    jasminOils,
    roseOils,
    lavenderOils,
    healingHerb,
    medicinalTincture,
    alacrityTincture,
    firePowderPot,
    smokePowderPot,
    flashPowderPot,
  ],

  upgradeProfiles: [
    // Weapon upgrades
    balancedLightWeaponUpgrade,
    balancedHeavyWeaponUpgrade,
    polishedWeaponUpgrade,
    stonewashedWeaponUpgrade,
    heatTreatmentUpgrade,
    temperedWeaponUpgrade,
    // Shield upgrades
    reinforcedShieldUpgrade,
    improvedEnarmes,
  ],
});

Object.assign(window, { ruleset: latest });
